import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { scanProfileText, uploadModeratedPhoto } from '../../util/api';
import { fileToDataUrl } from '../../util/profileModeration';
import {
  fetchCurrentUser,
  fetchCurrentUserHasListingsThunk,
  setCurrentUser,
} from '../../ducks/user.duck';

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file }, { rejectWithValue }) => {
    return fileToDataUrl(file)
      .then(imageBase64 => uploadModeratedPhoto({ imageBase64, purpose: 'profile' }))
      .then(resp => {
        const uploadedImage = resp?.data?.image;
        if (!uploadedImage) {
          throw new Error('Photo upload failed.');
        }
        return { id, uploadedImage };
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk
export const uploadImage = actionPayload => dispatch => {
  return dispatch(uploadImageThunk(actionPayload));
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': [
        'variants.square-small',
        'variants.square-small2x',
        'variants.square-xsmall',
        'variants.square-xsmall2x',
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.default',
        'variants.scaled-small',
        'variants.scaled-medium',
      ],
    };

    return scanProfileText({
      kind: 'profile',
      fields: {
        displayName: actionPayload.displayName,
        firstName: actionPayload.firstName,
        lastName: actionPayload.lastName,
        bio: actionPayload.bio,
        talkingNote: actionPayload.publicData?.talkingNote,
        interestedTopics: actionPayload.publicData?.interestedTopics,
        interestedTopicsOther: actionPayload.publicData?.interestedTopicsOther,
        ...Object.fromEntries(
          Object.entries(actionPayload.publicData || {}).filter(
            ([key, value]) =>
              !['talkingNote', 'interestedTopics', 'interestedTopicsOther'].includes(key) &&
              typeof value === 'string' &&
              value.trim()
          )
        ),
      },
    })
      .then(() => sdk.currentUser.updateProfile(actionPayload, queryParams))
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        // Keep a successful crop/upload so Save can retry without wiping the photo.
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
      });
  },
});

export const { clearUpdatedForm } = profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;

/**
 * Refresh own listings whenever /profile opens.
 *
 * @returns {Function}
 */
export const loadData = () => dispatch =>
  Promise.resolve(dispatch(fetchCurrentUser({ updateNotifications: false }))).then(() =>
    dispatch(fetchCurrentUserHasListingsThunk())
  );
